import React, { useEffect, useState } from 'react';
import LineChartPeaksPreview from '../shared/LineChartPeaksPreview';
import { useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import { selectMaximumStandardName, selectReportingPeaksArray, selectAllStdFilesData, selectAreaUnderPeaks, selectPeaksObject } from '../../lib/utilities/selector';
import { targetConcentrationPredictorAPI } from '../../api/api';
import { MdOutlineChevronRight } from "react-icons/md";
import { useDispatch } from 'react-redux';
import { savePeaksObject } from '../../reduxSlices/results/resultsSlice';
import { useNavigate } from 'react-router-dom'
import ResizableDiv from '../shared/ResizableDiv';
// import saveResultsToFirebase from '../../lib/utilities/saveResultsToFirebase';
import { v4 as uuidv4 } from 'uuid';
import sanitizeKeysDeep from '../../lib/utilities/sanitizeKeyDeep';
import ConfirmationModal from '../shared/ConfirmationModal';
import { useConfigNavigationGuard } from '../../hooks/useConfigNavigationGuard';
import { resetAll } from '../../reduxSlices/results/resultsSlice';





// Generate or retrieve from localStorage
const getOrCreateConfigId = () => {
  const existing = localStorage.getItem('currentConfigId');
  if (existing) return existing;

  const newId = uuidv4();
  localStorage.setItem('currentConfigId', newId);
  return newId;
};

const configId = getOrCreateConfigId();

function PeaksApproval() {


  const maximumStandardName = useSelector(selectMaximumStandardName);
  const reportingPeaksArray = useSelector(selectReportingPeaksArray);
  const AllStdFilesData = useSelector(selectAllStdFilesData);
  const areaUnderPeaksArray = useSelector(selectAreaUnderPeaks);
  const savedPeaksObject = useSelector(selectPeaksObject);
  // Additional arrays for Firebase saving
  const sigmaArray = useSelector((state) => state.results.sigmaArray);
  const gammaArray = useSelector((state) => state.results.gammaArray);
  const buggingPeaksArray = useSelector((state) => state.results.buggingPeaksArray);
  const allReportingPeaksArray = useSelector((state) => state.results.allReportingPeaksArray);

  const [initialPeaks, setInitialPeaks] = useState(null);
  const [selectedPeakIndex, setSelectedPeakIndex] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate()

  // Navigation guard
  const { showExitModal, handleConfirmExit, handleCancelExit } = useConfigNavigationGuard(
    ['/configuration/calibrationCurve', '/configuration/uploadUnkFiles'],
    () => dispatch(resetAll())
  );





  useEffect(() => {
    if (initialPeaks !== null) return;

    // 1) If we already saved peaks (including tc) in Redux, prefer those
    if (Array.isArray(savedPeaksObject) && savedPeaksObject.length > 0) {
      // Deep-clone to detach from Redux's frozen objects
      const cloned = savedPeaksObject.map((p) => ({
        ...p,
        visible: p.visible !== undefined ? p.visible : true, // Preserve or default visible flag
        tc: Array.isArray(p.tc) ? p.tc.map((tcObj) => ({ ...tcObj })) : [],
        stdTargetRSD: Array.isArray(p.stdTargetRSD) ? p.stdTargetRSD.map((rsdObj) => ({ ...rsdObj })) :
          // Initialize with empty RSD values if not present (backward compatibility)
          Array.isArray(p.tc) ? p.tc.map(tcObj => {
            const key = Object.keys(tcObj)[0];
            return { [key]: '' };
          }) : [],
        qcTc: Array.isArray(p.qcTc) ? p.qcTc.map((tcObj) => ({ ...tcObj })) : p.qcTc,
        unkConcentrations: Array.isArray(p.unkConcentrations)
          ? p.unkConcentrations.map((tcObj) => ({ ...tcObj }))
          : p.unkConcentrations,
        equation: p.equation ? { ...p.equation } : p.equation,
      }));
      setInitialPeaks(cloned);
      return;
    }

    // 2) Otherwise initialize fresh from reportingPeaksArray
    if (reportingPeaksArray.length > 0) {
      // Create the tc array with objects based on keys in AllStdFilesData
      const tcValues = AllStdFilesData.map(obj => {
        const key = Object.keys(obj)[0]; // Get the key from each object
        return { [key]: '' }; // Create an object with the key and an empty string as the value
      });

      const updatedPeaks = reportingPeaksArray.map((peak, index) => ({
        peak,
        show: true,
        visible: true, // Initialize visibility flag for delete functionality
        name: `Detected Peak ${index + 1}`,
        tc: tcValues, // Include the tc array with objects here
        stdTargetRSD: tcValues.map(obj => {
          const key = Object.keys(obj)[0];
          return { [key]: '' };
        }), // Add RSD array similar to tc
        active: false,
        equation: null,
      }));

      setInitialPeaks(updatedPeaks);
    }
  }, [initialPeaks, savedPeaksObject, reportingPeaksArray, AllStdFilesData]);




  const handlePeakActive = (index) => {
    if (initialPeaks !== null) {
      setInitialPeaks((prev) =>
        prev.map((peak, i) => (i === index ? { ...peak, active: !peak.active } : peak))
      );
    }
  };

  const handleCheckboxChange = (index) => {
    if (initialPeaks !== null) {
      setInitialPeaks((prev) =>
        prev.map((peak, i) => (i === index ? { ...peak, show: !peak.show } : peak))
      );
    }
  };

  const handleNameChange = (index, newName) => {
    if (initialPeaks !== null) {
      setInitialPeaks((prev) =>
        prev.map((peak, i) => (i === index ? { ...peak, name: newName } : peak))
      );
    }
  };



  const handleTCChange = (peakIndex, tcKey, newTC) => {
    if (initialPeaks !== null) {
      const updatedPeaks = initialPeaks.map((peak, idx) => {
        if (idx === peakIndex) {
          return {
            ...peak,
            tc: peak.tc.map(tcObj => {
              const key = Object.keys(tcObj)[0];
              if (key === tcKey) {
                // Update the user-modified field and mark it as edited
                return { [tcKey]: newTC === "" ? "" : parseFloat(newTC) || "", userEdited: true };
              }
              return tcObj; // Keep other fields intact
            }),
          };
        }
        return peak;
      });

      // Reset API-generated fields in the same column
      updatedPeaks[peakIndex].tc = updatedPeaks[peakIndex].tc.map(tcObj => {
        const key = Object.keys(tcObj)[0];
        if (key !== tcKey && !tcObj.userEdited) {
          // Reset only API-generated fields
          return { [key]: "", userEdited: false };
        }
        return tcObj;
      });

      setInitialPeaks(updatedPeaks); // Update state

      checkIfTwoOrMoreFilledInColumn(peakIndex, updatedPeaks); // Trigger recomputation
    }
  };

  const handleRSDChange = (peakIndex, rsdKey, newRSD) => {
    if (initialPeaks !== null) {
      const updatedPeaks = initialPeaks.map((peak, idx) => {
        if (idx === peakIndex) {
          return {
            ...peak,
            stdTargetRSD: peak.stdTargetRSD.map(rsdObj => {
              const key = Object.keys(rsdObj)[0];
              if (key === rsdKey) {
                return { [rsdKey]: newRSD === "" ? "" : parseFloat(newRSD) || "" };
              }
              return rsdObj;
            }),
          };
        }
        return peak;
      });
      setInitialPeaks(updatedPeaks);
    }
  };

  const checkIfTwoOrMoreFilledInColumn = async (peakIndex, peaks) => {
    const peak = peaks[peakIndex];
    if (peak) {
      // Count fields filled by the user
      const filledCount = peak.tc.reduce((count, tcObj) => {
        const value = Object.values(tcObj)[0];
        return value !== "" && !isNaN(value) ? count + 1 : count;
      }, 0);

      // Only proceed if 2 or more fields are filled
      if (filledCount >= 2) {
        const targetsObject = peak.tc.reduce((acc, tcObj) => {
          const key = Object.keys(tcObj)[0];
          acc[key] = tcObj[key]; // Collect all values (user-filled and empty strings)
          return acc;
        }, {});

        const { target_dict: predictedTargets, calibration_equation: calibrationEquation } = await targetConcentrationPredictorAPI(areaUnderPeaksArray[peakIndex], targetsObject);

        // Update only API-generated fields
        const updatedPeaks = peaks.map((peak, idx) => {
          if (idx === peakIndex) {
            return {
              ...peak,
              tc: peak.tc.map(tcObj => {
                const key = Object.keys(tcObj)[0];
                if (!tcObj.userEdited) {
                  // Replace empty fields with predicted values
                  return { [key]: predictedTargets[key] || "", userEdited: false };
                }
                return tcObj; // Preserve user-edited fields
              }),
              equation: calibrationEquation,
            };
          }
          return peak;
        });

        setInitialPeaks(updatedPeaks); // Update state
      }
    }
  };


  const handlePeakNameClick = (index) => {
    setSelectedPeakIndex(index);

  };



  const handleNextScreen = async () => {
    if (!isEqual(initialPeaks, savedPeaksObject)) {
      dispatch(savePeaksObject(initialPeaks));
    }

    try {
      // await saveResultsToFirebase(`${configId}/peaksObject`, sanitizeKeysDeep(initialPeaks));
    } catch (error) {
      console.error("❌ Error saving to Firebase:", error);
    }

    navigate('/configuration/calibrationCurve');
  };

  // const handleAutoFill = () => {
  //   if (initialPeaks !== null) {
  //     const predefinedValues = [
  //       [
  //         { "IC-CAL-1_CD.txt": 0.025 },
  //         { "IC-CAL-2_CD.txt": 0.1 },
  //         { "IC-CAL-3_CD.txt": 0.25 },
  //         { "IC-CAL-4_CD.txt": 1.25 },
  //         { "IC-CAL-5_CD.txt": 5 }
  //       ],
  //       [
  //         { "IC-CAL-1_CD.txt": 1 },
  //         { "IC-CAL-2_CD.txt": 4 },
  //         { "IC-CAL-3_CD.txt": 10 },
  //         { "IC-CAL-4_CD.txt": 50 },
  //         { "IC-CAL-5_CD.txt": 200 }
  //       ],
  //       [
  //         { "IC-CAL-1_CD.txt": 0.01 },
  //         { "IC-CAL-2_CD.txt": 0.04 },
  //         { "IC-CAL-3_CD.txt": 0.1 },
  //         { "IC-CAL-4_CD.txt": 0.5 },
  //         { "IC-CAL-5_CD.txt": 2 }
  //       ],
  //       [
  //         { "IC-CAL-1_CD.txt": 1 },
  //         { "IC-CAL-2_CD.txt": 4 },
  //         { "IC-CAL-3_CD.txt": 10 },
  //         { "IC-CAL-4_CD.txt": 50 },
  //         { "IC-CAL-5_CD.txt": 200 }
  //       ],
  //       [
  //         { "IC-CAL-1_CD.txt": 0.025 },
  //         { "IC-CAL-2_CD.txt": 0.1 },
  //         { "IC-CAL-3_CD.txt": 0.25 },
  //         { "IC-CAL-4_CD.txt": 1.25 },
  //         { "IC-CAL-5_CD.txt": 5 }
  //       ],
  //       [
  //         { "IC-CAL-1_CD.txt": 0.025 },
  //         { "IC-CAL-2_CD.txt": 0.1 },
  //         { "IC-CAL-3_CD.txt": 0.25 },
  //         { "IC-CAL-4_CD.txt": 1.25 },
  //         { "IC-CAL-5_CD.txt": 5 }
  //       ],

  //     ];

  //     const updatedPeaks = initialPeaks.map((peak, peakIndex) => {
  //       const predefinedPeakValues = predefinedValues[peakIndex] || [];
  //       return {
  //         ...peak,
  //         tc: peak.tc.map((tcObj) => {
  //           const key = Object.keys(tcObj)[0];
  //           const predefinedValueObj = predefinedPeakValues.find((item) => item[key] !== undefined);
  //           return {
  //             [key]: predefinedValueObj ? predefinedValueObj[key] : "", // Use predefined value if available, otherwise leave empty
  //             userEdited: false,
  //           };
  //         }),
  //       };
  //     });

  //     setInitialPeaks(updatedPeaks); // Update state
  //   }
  // };

  const handleClearAll = () => {
    if (initialPeaks !== null) {
      const clearedPeaks = initialPeaks.map((peak) => ({
        ...peak,
        tc: peak.tc.map((tcObj) => {
          const key = Object.keys(tcObj)[0];
          return {
            [key]: "",
            userEdited: false, // Reset the user-edited flag as well
          };
        }),
        stdTargetRSD: peak.stdTargetRSD.map((rsdObj) => {
          const key = Object.keys(rsdObj)[0];
          return {
            [key]: "",
          };
        }),
        // Clear fields that depend on target concentrations
        stdConcentrations: null,
        equation: null,
        qcTc: null,
        qcConcentrations: null,
        unkConcentrations: null,
      }));

      setInitialPeaks(clearedPeaks); // Update local state

      // Save the cleared peaks to Redux to remove target concentration dependent data
      dispatch(savePeaksObject(clearedPeaks));
    }
  };

  const allTcValuesFilled = initialPeaks
    ? initialPeaks.every((peak) =>
      peak.show === false ||
      peak.tc.every((tcObj) => Object.values(tcObj)[0] !== "")
    ) && !initialPeaks.every((peak) => peak.show === false)
    : false;




  return (
    <div className=' flex flex-col overflow-hidden' >
      <ConfirmationModal
        isOpen={showExitModal}
        onClose={handleCancelExit}
        onConfirm={handleConfirmExit}
        title="Leave Configuration?"
        message="If you leave now, the current configuration will not be saved and all progress will be lost. Are you sure you want to continue?"
        confirmText="Leave"
        cancelText="Stay"
        type="warning"
      />
      <ResizableDiv initialHeight={500} minHeight={400} className='rounded-sm shadow-md px-5 py-2 m-8 '>
        {initialPeaks && <LineChartPeaksPreview data={AllStdFilesData} initialVisibleChart={maximumStandardName} reportingPeaks={initialPeaks} onPeakNameClick={handlePeakNameClick} />}
      </ResizableDiv>

      <div className='flex-grow flex justify-center items-center overflow-auto mb-24'>
        <table className='border-separate border-spacing-0'>

          <tbody>
            <tr className='bg-gray-200'>
              <td className='text-center bg-customeBlack text-gray-200 rounded-tl-sm'>Peak Selection</td>
              {initialPeaks && initialPeaks.map((value, index) => (
                <td key={`select-${index}`} className={`${selectedPeakIndex === index ? 'bg-gray-300 border-l-2 border-r-2 border-gray-400' : 'border-l-2 border-r-2 border-gray-300'}`} style={{ textAlign: 'center', verticalAlign: 'middle' }}>

                  <label className="inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" checked={value.show} onChange={() => handleCheckboxChange(index)} />
                    <div className="relative w-11 h-6 bg-gray-200 rounded-full dark:bg-gray-300 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-cyan-500"></div>

                  </label>

                </td>
              ))}
            </tr>
            <tr className='bg-gray-200'>
              <td className='text-center bg-customeBlack text-gray-200'>Peak Name</td>
              {initialPeaks && initialPeaks.map((value, index) => (
                <td key={`name-${index}`} className={selectedPeakIndex === index ? 'bg-gray-300 border-l-2 border-r-2 border-gray-400' : 'border-l-2 border-r-2 border-gray-300'}>
                  <input
                    className='p-2 disabled:text-gray-300 w-36 rounded-sm shadow-sm text-gray-500 focus:outline-cyan-500'
                    value={value.name}
                    disabled={!value.show}
                    onChange={(e) => handleNameChange(index, e.target.value)}
                    onFocus={() => { setSelectedPeakIndex(index); handlePeakActive(index); }}
                    onBlur={() => { setSelectedPeakIndex(null); handlePeakActive(index); }}
                  />
                </td>
              ))}
            </tr>

            {AllStdFilesData.map((obj, tcIndex) => {
              const tcKey = Object.keys(obj)[0]; // Get the key for this row
              return (
                <tr key={`tc-row-${tcIndex}`} className='bg-gray-200'>
                  <td className='text-center bg-customeBlack text-gray-200'><span className='text-customeOrange font-bold'>{tcKey.split('.').slice(0, -1).join('.')}</span></td>
                  {initialPeaks && initialPeaks.map((value, peakIndex) => (
                    <td key={`tc-${tcIndex}-${peakIndex}`} className={`${selectedPeakIndex === peakIndex ? 'bg-gray-300 border-l-2 border-r-2 border-gray-400' : 'border-l-2 border-r-2 border-gray-300'} p-2`}>
                      <div className='flex flex-col gap-2'>
                        <div className='flex items-center gap-1'>
                          <input
                            className='p-2 w-32 rounded-sm shadow-sm text-gray-500 focus:outline-cyan-500 disabled:text-gray-300'
                            value={value.tc.find(tcObj => tcKey in tcObj)[tcKey] || ''}
                            type="number"
                            disabled={!value.show}
                            placeholder="Target Conc."
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              const parsedValue = inputValue === '' ? '' : parseFloat(inputValue);
                              handleTCChange(peakIndex, tcKey, parsedValue);
                            }}
                            onFocus={() => { setSelectedPeakIndex(peakIndex); handlePeakActive(peakIndex); }}
                            onBlur={() => { setSelectedPeakIndex(null); handlePeakActive(peakIndex); }}
                          />
                          <span className='text-sm text-gray-600 font-medium'>ppm</span>
                        </div>
                        <div className='flex items-center gap-1'>
                          <input
                            className='p-2 w-32 rounded-sm shadow-sm text-gray-500 focus:outline-cyan-500 disabled:text-gray-300'
                            value={value.stdTargetRSD?.find(rsdObj => tcKey in rsdObj)?.[tcKey] || ''}
                            type="number"
                            disabled={!value.show}
                            placeholder="Target RSD"
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              const parsedValue = inputValue === '' ? '' : parseFloat(inputValue);
                              handleRSDChange(peakIndex, tcKey, parsedValue);
                            }}
                            onFocus={() => { setSelectedPeakIndex(peakIndex); handlePeakActive(peakIndex); }}
                            onBlur={() => { setSelectedPeakIndex(null); handlePeakActive(peakIndex); }}
                          />
                          <span className='text-sm text-gray-600 font-medium'>%</span>
                        </div>
                      </div>
                    </td>
                  ))}
                </tr>
              );
            })}

          </tbody>
        </table>
      </div>
      <div className='flex fixed bottom-8 right-8 z-40 gap-2'>
        <div className='flex items-center justify-center'>
          {/* <IoMdArrowBack /> */}
          {/* <button 
                    onClick={() => handleAutoFill()}
                    className='text-lg text-indigo-700 mr-10'>Auto Fill
                </button> */}
          <button
            onClick={() => handleClearAll()}
            className='text-lg text-indigo-700 mr-10'>Clear All
          </button>
        </div>

        <button
          disabled={!allTcValuesFilled}
          onClick={() => handleNextScreen()}
          className={`flex-row flex items-center justify-center bg-buttonColorActive text-white font-semibold w-32 p-3 rounded-md self-center disabled:bg-gray-300 disabled:text-gray-500 disabled:border-gray-300 disabled:cursor-not-allowed hover:bg-customeOrange hover:text-customeBlack`}
        >Next
          <MdOutlineChevronRight className='ml-6' fontSize={24} />
        </button>
      </div>


    </div>

  )
}

export default PeaksApproval


